/**
 * F010: ACC Generate Modal
 *
 * Modal for generating an Annual Compliance Certificate checklist.
 * Inputs: compliance year + annual compliance date.
 * Preview: lodgement due date (compliance date + 45 days).
 */

import { useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// ============================================================================
// TYPES
// ============================================================================

export interface GenerateAccResult {
  complianceYear: number;
  annualComplianceDate: string;
  lodgementDueDate: string;
  totalItems: number;
  itemIds: string[];
}

interface AccGenerateModalProps {
  open: boolean;
  onClose: () => void;
  onGenerated: (result: GenerateAccResult) => void;
}

// ============================================================================
// HELPERS
// ============================================================================

function calcLodgementDue(complianceDateStr: string): string {
  try {
    const dateObj = parseISO(complianceDateStr);
    return format(addDays(dateObj, 45), "dd MMM yyyy");
  } catch {
    return "-";
  }
}

// ============================================================================
// COMPONENT
// ============================================================================

export function AccGenerateModal({
  open,
  onClose,
  onGenerated,
}: AccGenerateModalProps) {
  const { toast } = useToast();
  const currentYear = new Date().getFullYear();

  const [accYear, setAccYear] = useState<number>(currentYear);
  const [complianceDate, setComplianceDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const lodgementPreview = complianceDate ? calcLodgementDue(complianceDate) : null;

  const handleClose = () => {
    setAccYear(currentYear);
    setComplianceDate("");
    onClose();
  };

  const handleSubmit = async () => {
    if (!complianceDate) {
      toast({
        title: "Annual compliance date is required",
        variant: "destructive",
      });
      return;
    }

    if (!accYear || isNaN(accYear) || accYear < 2000 || accYear > 2200) {
      toast({ title: "Please enter a valid year (2000–2200)", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const response = await apiRequest("POST", "/api/compliance/acc/generate", {
        accYear,
        annualComplianceDate: complianceDate,
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(
          (body as { error?: string }).error ?? `Server error ${response.status}`
        );
      }

      const result = (await response.json()) as GenerateAccResult;
      toast({
        title: `ACC Checklist ${accYear} generated`,
        description: `${result.totalItems} items created. Lodgement due ${lodgementPreview ?? result.lodgementDueDate}.`,
      });
      onGenerated(result);
      handleClose();
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to generate ACC checklist";
      toast({ title: message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent data-testid="modal-acc-generate" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Generate ACC Checklist</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Year */}
          <div className="space-y-1">
            <Label htmlFor="acc-year">Compliance Year *</Label>
            <Input
              id="acc-year"
              data-testid="input-acc-year"
              type="number"
              min={2000}
              max={2200}
              value={accYear}
              onChange={(e) => setAccYear(parseInt(e.target.value, 10))}
            />
          </div>

          {/* Annual Compliance Date */}
          <div className="space-y-1">
            <Label htmlFor="compliance-date">Annual Compliance Date *</Label>
            <Input
              id="compliance-date"
              data-testid="input-compliance-date"
              type="date"
              value={complianceDate}
              onChange={(e) => setComplianceDate(e.target.value)}
            />
          </div>

          {/* Lodgement due preview */}
          {lodgementPreview && (
            <div
              className="rounded-md bg-muted p-3 text-sm"
              data-testid="lodgement-due-preview"
            >
              <span className="text-muted-foreground">Lodgement due: </span>
              <span className="font-medium">{lodgementPreview}</span>
              <span className="ml-1 text-xs text-muted-foreground">
                (45 days after compliance date)
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            data-testid="button-submit-generate"
            onClick={handleSubmit}
            disabled={submitting || !complianceDate}
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              "Generate"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
